import type { BrandStyleTokens, StyleStrengthTier } from '~/design-system/components/DeckGen/ai/deckFlowPrompt'
import { styleStrengthTier } from '~/design-system/components/DeckGen/ai/deckFlowPrompt'
// The codebase's hex parser, reused rather than re-typed. birtvyBrandTruth is the module that already owns
// colour arithmetic for scraped brands; it is pure above its `--- canvas sampling ---` line and touches the
// DOM only inside function bodies, so importing it keeps this module's node/browser promise.
import { parseHex } from '~/design-system/components/DeckGen/birtvy/web/birtvyBrandTruth'
// The product's own logo-contrast decision, already measured and already shipped at bake time. Restating it
// here would be a second opinion on the same question, and the two would diverge on the first brand that
// disagreed with them.
import { markReadsOnBackground } from '~/design-system/components/DeckGen/utils/brandLogoContrast'
// Type-only, so it erases at build and this module keeps its "pure, reaches the browser" promise — nothing
// from the lab is imported at runtime. The mode is DEFINED there because the session record is what has to
// store it; defining a second copy here is how two enums drift apart.
import type { StyleLabLayoutMode } from './lab/styleLabSession'
import {
  normalizeHex,
  type LayoutPattern,
  type Style,
  type StyleDensity,
  type StyleDepth,
  type StyleVoiceExample,
} from './styleRecord'
// The harvest: what a real page actually shipped, so an artifact can reuse the site's OWN pictures and
// treatments instead of drawing a gradient from memory. Type-only — the harvest module owns the shape and
// this one only reads it.
import type { SiteAssets } from './styleSiteAssets'
// The five role names and their order, largest first. Runtime, and cheap: `typeScale.ts` imports nothing.
import { TYPE_ROLES, type TypeRole, type TypeScale } from './typeScale'

/**
 * RENDERERS — one {@link Style} into the shapes something downstream can actually consume.
 *
 *   styleToPromptBlock       → the slide-generation block, numbers included
 *   styleToArtifactVars      → the artifact `--a-*` vocabulary
 *   styleToThemeRecipe       → the artifact THEME CONTEXT: tokens, rules, the mark, the site's own material
 *   styleToBrandStyleTokens  → the LIVE generation contract (`BrandStyleTokens`), unchanged
 *   artifactAngle            → one line per document in a multi-document run, so three are three
 *
 * `styleRecord.ts` encodes "every source is an importer, never a format". This is the other half: every
 * CONSUMER reads a `Style` and nothing else. Adding a fourth renderer must not touch an importer.
 *
 * WHY THE LIVE-CONTRACT PROJECTION EXISTS AT ALL. `Style` could have replaced `BrandStyleTokens` and every
 * serializer behind it. It deliberately does not: `/deckgen-flow-api` → `buildBrandStyleBlock` is the path
 * every deck on the canvas is generated through today, it works, and a big-bang swap would be untestable.
 * Projecting INTO the live shape lets the playground run the new record against the real generator and
 * produce evidence. Flipping generation onto `Style` is a later, smaller change made on that evidence.
 *
 * ONE PATH PER CALL — the one rule a caller can get wrong. `styleToPromptBlock` is COMPLETE: it states the
 * palette, the fonts and the logo as well as the numbers, because for an artifact request there is no token
 * channel at all. `styleToBrandStyleTokens` feeds a route that builds its own `## Brand style` block from
 * those same colours and fonts. Send both into one generation and every colour is stated twice, which a
 * model reads as emphasis. Pick the channel: tokens into the live route, OR this block.
 *
 * WORDING IS INHERITED ON PURPOSE. The colour, font and logo lines are the ones `buildBrandStyleBlock` and
 * `buildMoodboardPromptBlock` already ship, near-verbatim, down to the strength framing. That phrasing has
 * been tuned against real generations; rewriting it would mean re-learning which sentences a model obeys.
 * What this renderer ADDS is what neither could structurally say: a spacing step, a fill budget, a radius
 * set, a shadow position and a named layout list — values `styleEval.server.ts` can score, which prose is
 * exactly what you cannot do with an adjective.
 *
 * PURE. Same rule as the importers: no React, no fetch, no clock.
 */

/** 6-digit hex, the only colour form that reaches a prompt or a token (importers normalise to it). */
const HEX_RE = /^#[0-9a-f]{6}$/i

function isHex(value: string): boolean {
  return HEX_RE.test(value)
}

/** First finite number in the list, or undefined. `0` is a real value (a square corner), so this cannot be
 *  a truthiness chain. */
function firstNumber(values: (number | undefined)[]): number | undefined {
  for (const value of values) {
    if (value != null && Number.isFinite(value)) return value
  }
  return undefined
}

/** A family name as a CSS font stack. `sans-serif` is the generic the live brand block appends to every
 *  family, and matching it means a Style-driven slide and a BrandStyle-driven slide fall back to the same
 *  face when the family fails to load. Quotes and backslashes are stripped rather than escaped: a family
 *  name carrying them is a broken import, not a value worth round-tripping into a stylesheet. */
function fontStack(family: string | undefined): string | undefined {
  // The FIRST family only. A kit written by the PDF read held `headingFont: "Inter, sans-serif"`, a
  // stack where a name belongs; quoted whole it named a family no browser has, and the Concept
  // relationship document (Deck core, 2026-09-05) carried `'Inter, sans-serif', sans-serif` verbatim and
  // rendered in the system face. `resolveFamily` now strips the stack on the way in; this is the guard
  // for a `type.display` that reached here another way.
  const clean = family
    ?.split(',')[0]
    ?.replace(/["'\\]/g, '')
    .trim()
  if (!clean) return undefined
  return `'${clean}', sans-serif`
}

/**
 * Display tracking as a slide-safe em value.
 *
 * Sources measure tracking against 16px body text, often at -0.02 to -0.05em. Applied verbatim to a 120px
 * title it CRUSHES the letterforms — the measured bug `sanitizeHeadingLetterSpacing` exists to fix in
 * deckFlowPrompt. Identical clamp bounds here on purpose: a Style-driven title must never come out tighter
 * than a BrandStyle-driven one, or a playground A/B is comparing two different type treatments.
 */
function safeTracking(tracking: number | undefined): string | undefined {
  if (tracking == null || !Number.isFinite(tracking)) return undefined
  const clamped = Math.max(-0.01, Math.min(0.1, tracking))
  return `${Math.round(clamped * 1000) / 1000}em`
}

/** The framing (header / intro / footer) for a strength tier. Copied from `brandStrengthFraming` in
 *  deckFlowPrompt, which is module-private: this is the proven wording, and restating it verbatim is what
 *  keeps a lab A/B a test of the TOKENS rather than of an accidental rewrite of the framing. */
function strengthFraming(tier: Exclude<StyleStrengthTier, 'off'>): { header: string; intro: string; footer: string } {
  const layoutRule = 'Keep every layout rule (flex/grid + gap, no overlap, fit 1920×1080).'
  switch (tier) {
    case 'close':
      return {
        header: '## Brand style — strongly shapes the Color and Typography below',
        intro: `Design this deck to clearly resemble the brand below: adopt this palette and these fonts closely. You may adjust slightly for legibility and content fit, but the deck should read as on-brand. ${layoutRule}`,
        footer: 'Keep a consistent on-brand feel: brand background tone throughout, accent used sparingly, strong text/background contrast.',
      }
    case 'light':
      return {
        header: '## Brand style — a LIGHT reference (do NOT copy exactly)',
        intro: `Draw light inspiration from the brand below — its mood, one or two of its colors, its general typographic feel. You may deviate freely for content fit and legibility, you need NOT match the fonts exactly, and the clean default design is fine wherever it reads better. ${layoutRule}`,
        footer: 'Treat this as a loose nod, not a strict brand system.',
      }
    case 'faint':
      return {
        header: '## Brand style — a FAINT reference only',
        intro: `Take only a faint nod from the brand below (perhaps one accent color or a hint of its mood). Prioritize clean, legible, default design over matching this style. ${layoutRule}`,
        footer: 'A distant influence only — do not let it compromise clarity.',
      }
    case 'strict':
    default:
      return {
        header: '## Brand style — OVERRIDES the default Color and Typography rules above',
        intro: `Design this ENTIRE deck in the brand below. IGNORE the default zinc palette and the Inter type scale; use these brand tokens instead. ${layoutRule}`,
        footer: 'Make every slide feel on-brand and consistent: same background tone throughout, accent used sparingly, strong text/background contrast. Do not invent colors outside this palette.',
      }
  }
}

/** How strictly the free art direction is framed. Verbatim from `buildMoodboardPromptBlock` — the prose half
 *  of a style has always been tiered separately from the token half, and it still is. */
const DIRECTION: Record<Exclude<StyleStrengthTier, 'off'>, string> = {
  strict: 'Style direction (follow strictly):',
  close: 'Style direction (follow closely; prioritize legibility where they conflict):',
  light: 'Style direction (light inspiration only — do not copy; deviate freely for content fit):',
  faint: 'Style direction (a faint nod only — prioritize clean, legible default design):',
}

/** Density as a budget the model can check itself against, rather than an adjective it can feel differently
 *  about on Tuesday. The percentages are of the usable height the flow prompt already has it compute
 *  ("Usable height = 1080 − 2×padding"), so this rides an arithmetic the prompt already performs. */
const DENSITY_FILL: Record<StyleDensity, string> = {
  tight: 'fill roughly 80% of the usable height',
  normal: 'fill roughly 70% of the usable height',
  airy: 'fill no more than 55% of the usable height',
}

const DENSITY_RULE: Record<StyleDensity, string> = {
  tight: 'Small gaps, more content per slide, little empty space — the closeness is the look.',
  normal: 'Clear separation between blocks, no drama.',
  airy: 'The empty space IS the design: cut content before you cut air.',
}

/** Gaps that read as each density, in multiples of the spacing step. Multiples rather than a sentence,
 *  because once `space.unit` is known they resolve to a real px range. */
const DENSITY_STEPS: Record<StyleDensity, [number, number]> = {
  tight: [2, 3],
  normal: [3, 5],
  airy: [6, 10],
}

const DEPTH_RULE: Record<StyleDepth, string> = {
  none: 'NO shadows anywhere — not on cards, not on text, not on images. Depth comes from spacing, contrast and hairlines. A box-shadow is a violation of this style.',
  soft: 'Shadows are soft and low-contrast: wide blur, near-zero spread, under 15% opacity. Never a hard edge.',
  hard: 'Shadows are hard: zero blur, a solid offset in a palette colour (e.g. 6px 6px 0 <accent>). Never a soft blur.',
}

/** What each structure IS, in one line the generator can build from. Naming the pattern without describing
 *  it just moves the ambiguity: "orbit" means nothing to a model asked to write flex CSS. The descriptions
 *  are the ones `deckJudgment.server.ts` scores against, so the thing that PICKS a layout and the thing that
 *  GRADES one are reading the same sentence. */
export const PATTERN_RULE: Record<LayoutPattern, string> = {
  split: 'split — two halves in tension, and the tension is the point (not merely two columns of equal stuff)',
  spectrum: 'spectrum — an ordered range read left to right, position carries meaning',
  field: 'field — items scattered in a bounded space, density carries meaning',
  orbit: 'orbit — one centre with satellites arranged around it',
  ledger: 'ledger — aligned rows of fact, columns lined up so the eye can scan down',
  'single-number': 'single-number — one figure given the whole frame, everything else subordinate',
  stack: 'stack — vertical rhythm, no columns',
  grid: 'grid — regular repeating cells, every cell the same internal layout',
  'full-bleed': 'full-bleed — image or colour to every edge, content sitting on top',
}

/**
 * The structures in the order `styleRecord.ts` declares them.
 *
 * Not a ranking, and deliberately not shuffled: every model in a lab run must receive a byte-identical
 * prompt, so a stable order is worth more than defeating position bias by chance. The "at least four
 * different" rule below is what actually stops a model settling on whichever one it read first.
 *
 * Written out rather than derived from `Object.keys(PATTERN_RULE)`, because that returns `string[]` and the
 * cast back to `LayoutPattern[]` would be the one place a pattern with no rule could reach a prompt.
 */
export const PATTERN_ORDER: LayoutPattern[] = [
  'split',
  'spectrum',
  'field',
  'orbit',
  'ledger',
  'single-number',
  'stack',
  'grid',
  'full-bleed',
]

/**
 * WHEN to reach for each structure, in terms of the CONTENT rather than the picture.
 *
 * `PATTERN_RULE` says what a structure is; a model that knows what a ledger looks like still has to decide
 * which slide is a ledger, and that decision is the one measured to be missing. Across 5 brands × 20
 * generations (2026-08-13) `layout.patterns` came back EMPTY from every source, so nothing ever answered
 * "what shape is this slide?" and the shared system prompt answered it identically every time — five brands
 * produced five recolourings of one deck. An unanswered field is not answered freely; it is answered by
 * whatever else is in the prompt.
 */
export const PATTERN_WHEN: Record<LayoutPattern, string> = {
  split: 'the artifact sets two things against each other — before and after, cost and benefit, them and us',
  spectrum: 'the values sit in a real order — stages, a scale, a timeline, low to high',
  field: 'there are many small items and their spread is the argument — a market, a landscape, a set of marks',
  orbit: 'one thing carries everything else — a platform and its integrations, a claim and its evidence',
  ledger: 'the rows share columns — pricing, a comparison, a schedule, line items',
  'single-number': 'one figure IS the argument and every other word in the artifact is its caption',
  stack: 'one claim develops downward and nothing is being compared',
  grid: 'the items are genuine peers, each read the same way — features, people, cards',
  'full-bleed': 'an image or a field of colour carries the feeling and there are very few words',
}

/**
 * Deck's own judgment on lists, near-verbatim from `deckJudgment.server.ts` ("Never propose a bulleted list;
 * a list is what you write when you have not found the device").
 *
 * RESTATED, NOT IMPORTED: that file is `.server.ts` precisely so its prompt never reaches a browser bundle,
 * and this module does. Keeping the two in one vocabulary is the whole point — the thing that PICKS a layout
 * and the thing that GRADES one have to mean the same words, or a good deck loses to a rubric that wanted a
 * different name for the same shape.
 */
export const NO_LIST_RULE =
  'A bulleted list is not a structure: it is what you write when you have not found the device. Reaching for one means you have not finished choosing.'

function colorLines(style: Style): string[] {
  const { bg, surface, text, muted, accent, border, palette } = style.color
  const lines: string[] = []
  if (bg) lines.push(`- Background (use on EVERY slide for a consistent look): ${bg}`)
  if (surface) lines.push(`- Cards / panels (surface): ${surface}`)
  if (text) lines.push(`- Body & heading text on the brand background: ${text}`)
  if (muted) lines.push(`- Muted / secondary text: ${muted}`)
  if (accent)
    lines.push(
      `- Accent (the brand color — use it deliberately for emphasis: key numbers, one highlighted word, a small shape, a divider, an icon; NEVER flood a whole slide with it): ${accent}`,
    )
  if (border) lines.push(`- Borders / dividers / hairlines: ${border}`)
  const hexes = palette.filter(isHex)
  if (hexes.length) lines.push(`- Full brand palette you may draw from: ${hexes.join('  ')}`)
  return lines
}

function typeLines(style: Style): string[] {
  const { display, body, displayWeights, scale, tracking, leading } = style.type
  const lines: string[] = []

  // CSS facts about HOW this style sets its headings. They carry the look even when the family itself is one
  // the renderer has to substitute, which is why they are emitted whether or not a family is known.
  const traits: string[] = []
  const shipped = (displayWeights ?? []).filter((w) => /^\d{3}$/.test(w)).sort((a, b) => Number(a) - Number(b))
  // Faux-bold guard: when the display face ships specific weights, ask for the heaviest one it HAS. Anything
  // else gets synthesized by the browser — thicker strokes, unchanged advances, the classic crushed-title
  // look on a single-weight display face.
  if (shipped.length > 0) traits.push(`font-weight:${shipped[shipped.length - 1]}`)
  if (style.type.case === 'upper') traits.push('text-transform:uppercase')
  const letterSpacing = safeTracking(tracking)
  if (letterSpacing) traits.push(`letter-spacing:${letterSpacing}`)
  if (leading != null && Number.isFinite(leading)) traits.push(`line-height:${leading}`)

  const stack = fontStack(display)
  if (stack || traits.length > 0) {
    const family = stack ? `use font-family:${stack}` : 'match the reference typography'
    const suffix =
      traits.length > 0 ? ` and set ${traits.join('; ')} on EVERY title — the brand's exact headline treatment` : ''
    lines.push(`- Heading font: ${family} on titles/Display/H1/H2${suffix}.`)
    if (shipped.length > 0) {
      lines.push(
        `- The heading font's files ship ONLY weight${shipped.length === 1 ? '' : 's'} ${shipped.join(', ')}. NEVER set any other font-weight on it — the browser would fake the bold and crush the letterforms. It is a display face: it already looks heavy at its shipped weight.`,
      )
    }
    lines.push(
      '- Keep display type READABLE: never set letter-spacing tighter than the value above (or than -0.01em when none is given) — brand display fonts already carry their own tight metrics; crushing them further makes titles illegible.',
    )
  }

  const bodyStack = fontStack(body)
  if (bodyStack) lines.push(`- Body font: use font-family:${bodyStack} on body/caption text.`)

  // The ratio, NOT an absolute ladder. Deriving five sizes from the ratio was tried and rejected: the live
  // five-level table (Display 120, H1 60, H2 36, Body 30, Caption 24) is not geometric — 24→30 is 1.25 but
  // 60→120 is 2 — so a geometric ladder anchored on the 30px body puts a cover title at ~59px with a typical
  // measured ratio of 1.25. That is a visible regression dressed up as precision. The ratio governs the
  // DISTANCE between the levels the slide picks, and that is what gets stated.
  if (scale != null && Number.isFinite(scale) && scale > 1) {
    lines.push(
      `- Type scale: this brand steps by ×${scale} between levels. Hold that ratio between the sizes on a slide — the smaller the ratio the calmer the page, the larger the more the title towers. Never pick a size between two steps.`,
    )
  }
  return lines
}

function spaceLines(style: Style): string[] {
  const lines: string[] = []
  const unit = style.space.unit
  const hasUnit = unit != null && Number.isFinite(unit) && unit > 0
  if (hasUnit) {
    const steps = [1, 2, 3, 4, 6, 8].map((m) => `${m * unit}`).join(', ')
    lines.push(
      `- Spacing step: ${unit}px. EVERY gap and padding value is a whole multiple of it — ${steps}. Never a value off this step.`,
    )
  }
  const density = style.space.density
  if (density) {
    const [lo, hi] = DENSITY_STEPS[density]
    // With a step this becomes an instruction you could check with a ruler; without one the fill budget alone
    // still beats the adjective.
    const gap = hasUnit ? `, gaps of ${lo * unit}-${hi * unit}px between blocks` : ''
    lines.push(`- Density (${density}): ${DENSITY_FILL[density]}${gap}. ${DENSITY_RULE[density]}`)
  }
  return lines
}

function radiusLines(style: Style): string[] {
  const { sm, md, lg } = style.radius
  // md leads: it is the card/panel radius, the one shape a slide actually repeats, and the value the live
  // pipeline carries as `typography.radius`.
  if (firstNumber([md, lg, sm]) == null) return []
  const values = [sm, md, lg].filter((v): v is number => v != null && Number.isFinite(v))
  return [
    `- Corner radius: ${values.map((v) => `${v}px`).join(' / ')}. Every card, panel, chip and image uses one of these; never another value.`,
  ]
}

function shadowLines(style: Style): string[] {
  const lines: string[] = []
  if (style.shadow.depth) lines.push(`- Depth: ${DEPTH_RULE[style.shadow.depth]}`)
  const measured = (style.shadow.values ?? []).map((v) => v.trim()).filter((v) => v !== '')
  if (measured.length) lines.push(`- Use these exact box-shadow values: ${measured.join(' | ')}.`)
  return lines
}

function layoutLines(style: Style): string[] {
  const phrases = style.layout.patterns.map((p) => PATTERN_RULE[p]).filter((p) => p !== undefined)
  if (phrases.length === 0) return []
  return [
    "- Layout structures, best first — pick the one that fits the slide's argument, and never fall back to a bulleted list:",
    ...phrases.map((p) => `  - ${p}`),
  ]
}

function assetLines(style: Style): string[] {
  const logo = style.assets.logoUrl
  if (!logo) return []
  // Verbatim from the live block: placing the mark exactly once, in flow, is the instruction that stopped
  // logos appearing on every slide and floating out of the content column.
  return [
    `- Brand logo: place it exactly ONCE, on the COVER slide only — a small mark as the FIRST element of the cover's content column: <img data-el="image" data-image-id="brand-logo" src="${logo}" style="height:44px;width:auto;display:block">. It lives in the normal flow (never absolute), aligned with the title block, never inside a plate, box or pill, never on a background you add, and never recoloured, cropped or redrawn. No logo on any other slide.`,
  ]
}

/**
 * THE STANDING RULES, as their own block.
 *
 * Their own section and not part of Voice, for two reasons that are both about survival. The Voice section
 * carries a 1,200-char guideline that a moodboard synthesis fills to the brim, so a rule folded in behind it
 * is a rule that stops arriving the moment the art direction gets long. And rules are the one thing in this
 * recipe the user said OUT LOUD, after seeing a document that got it wrong; they outrank an inference, and a
 * heading is how a model is told so.
 *
 * They also never travel through `layout.patterns`: see the note on `StyleVoice.rules`.
 */
function ruleLines(style: Style): string[] {
  const rules = (style.voice.rules ?? []).map((r) => r.trim()).filter((r) => r !== '')
  if (rules.length === 0) return []
  const lead =
    'Standing rules for this brand. Each one is a correction the user already made once, so they outrank every inference above. Follow all of them.'
  const kept: string[] = []
  let used = lead.length
  for (const rule of rules) {
    const line = `- ${rule}`
    if (used + line.length + 1 > MAX_RECIPE_RULES) break
    kept.push(line)
    used += line.length + 1
  }
  const over = rules.length - kept.length
  if (over > 0) kept.push(`(${over} more rule${over === 1 ? '' : 's'} in the kit)`)
  return [lead, ...kept]
}

/** How many written examples of the voice ride. Four, down from six on 2026-09-05 to pay for the five size
 *  lines and the `## System` lock inside the same budget; past a handful the model starts pattern-matching
 *  the examples' subject matter instead of their tone anyway. */
const MAX_RECIPE_EXAMPLES = 4

/** One example, clipped. An example is a specimen line, not an essay, and an unbounded `sample` pasted out
 *  of a document would spend the Voice section's whole share of the budget on one entry. */
const MAX_EXAMPLE_CHARS = 200

const EXAMPLE_LABEL: Record<StyleVoiceExample['kind'], string> = {
  do: 'Do',
  dont: 'Never',
  sample: 'Sample',
}

/** The written examples, as lines. Kept beside the tone because that is what they are FOR: "confident,
 *  plain, no hype" is an adjective a model can agree with and still not write, and a real line it can. */
function exampleLines(style: Style): string[] {
  const examples = (style.voice.examples ?? []).filter((e) => e.text.trim() !== '').slice(0, MAX_RECIPE_EXAMPLES)
  if (examples.length === 0) return []
  return [
    'Examples of this voice. Match how these are written, never what they say:',
    ...examples.map((e) => {
      const text = e.text.trim()
      const clipped = text.length > MAX_EXAMPLE_CHARS ? `${text.slice(0, MAX_EXAMPLE_CHARS)}...` : text
      const slot = e.slot?.trim() ? ` (${e.slot.trim()})` : ''
      const note = e.note?.trim() ? ` [${e.note.trim()}]` : ''
      return `- ${EXAMPLE_LABEL[e.kind]}${slot}: "${clipped}"${note}`
    }),
  ]
}

/**
 * The recipe's `## Voice`: tone, the written examples, keywords, avoids — and NOT the guideline, which is
 * `## Art direction` and droppable. `voiceLines` below is the artifact block's and still carries the guideline
 * inline, because that block is pinned byte for byte as the layout experiment's floor.
 */
function recipeVoiceLines(style: Style): string[] {
  const { tone, keywords, avoids } = style.voice
  const lines: string[] = []
  if (tone?.trim()) lines.push(`Tone of voice (apply to ALL copy you write): ${tone.trim()}`)
  lines.push(...exampleLines(style))
  const words = keywords.filter((k) => k.trim() !== '')
  if (words.length) lines.push(`Style keywords: ${words.join(', ')}`)
  const nots = avoids.filter((a) => a.trim() !== '')
  if (nots.length) lines.push(`Never use: ${nots.join(', ')}`)
  return lines
}

/** The prose half. Kept as prose because tone and "what to avoid" have no unit — see StyleVoice. */
function voiceLines(style: Style, tier: Exclude<StyleStrengthTier, 'off'>): string[] {
  const { tone, keywords, avoids, guideline } = style.voice
  const lines: string[] = []
  const direction = guideline?.trim()
  if (direction) lines.push('', DIRECTION[tier], direction)
  if (tone?.trim()) lines.push('', `Tone of voice (apply to ALL copy you write): ${tone.trim()}`)
  const examples = exampleLines(style)
  if (examples.length) lines.push('', ...examples)
  const words = keywords.filter((k) => k.trim() !== '')
  if (words.length) lines.push('', `Style keywords: ${words.join(', ')}`)
  const nots = avoids.filter((a) => a.trim() !== '')
  if (nots.length) lines.push('', `Never use: ${nots.join(', ')}`)
  return lines
}

/**
 * One slide's decided shape, as the `planned` pass hands it over.
 *
 * `pattern` is the ENUM, where the session record stores a bare `string` (`StyleLabConfig.layoutPlan`). The
 * difference is deliberate and each side is right: a session is a historical file that must still parse after
 * the enum changes, while this is the thing that gets RENDERED, and a pattern with no rule to state has
 * nothing to say to a model. Callers narrow at the boundary; the builder below skips an entry it cannot
 * describe rather than emitting a bare word.
 */
export interface LayoutPlanEntry {
  slide: number
  pattern: LayoutPattern
  why: string
  /** The ONE idea this slide carries. Optional only so the older two-field callers still compile.
   *
   *  It was being computed by the planner and then dropped on the way here, which quietly halved the
   *  experiment: a model told "slide 3 is a LEDGER" with no idea what belongs on it will put whatever it
   *  likes there, so `planned` differed from `vocabulary` in shape but not in content. Choosing the shape
   *  for what the slide carries IS the Chronicle model — without this the plan is decoration. */
  carries?: string
}

/**
 * Options for {@link styleToPromptBlock}.
 *
 * `strength` was the whole second argument until the layout experiment needed two more knobs, and the bare
 * number is STILL accepted (`styleToPromptBlock(style, 60)`) because the live callers pass one. A signature
 * change that forces an edit in another module is a change to that module, and this one is meant to be
 * additive: every existing call keeps returning the same bytes.
 */
export interface StylePromptOptions {
  /** 0–100, tiered exactly as the live pipeline tiers it. Governs the STYLE — see the layout note below. */
  strength?: number
  /** Absent reads as `off`, which emits today's block byte for byte: the floor the experiment measures against. */
  layoutMode?: StyleLabLayoutMode
  /** `planned` only. Emitted verbatim, in the order given. */
  plan?: LayoutPlanEntry[]
}

/** The one place the two call shapes converge, so nothing below has to know which one arrived. */
function promptOptions(opts: number | StylePromptOptions | undefined): StylePromptOptions {
  return typeof opts === 'number' ? { strength: opts } : (opts ?? {})
}

/**
 * The VOCABULARY section: the whole menu, the content that should trigger each item, the two repetition
 * constraints, and the list ban.
 *
 * The constraints are stated as arithmetic a model can check itself against ("never twice running", "at least
 * four different"), the same discipline the spacing step and the fill budget already follow. "Vary the
 * layouts" is an adjective — a model can believe it has complied while emitting nine stacks.
 */
function vocabularyLines(style: Style): string[] {
  const lines = [
    '',
    '## Layout — the structure carries the argument',
    `Every slide is built on ONE of these ${PATTERN_ORDER.length} structures. Choose it from what the slide has to say, then build it with the flex/grid rules above.`,
    ...PATTERN_ORDER.map((pattern) => `- ${PATTERN_RULE[pattern]}. Reach for it when ${PATTERN_WHEN[pattern]}.`),
    'Rules, not preferences:',
    '- Never use the same structure on two slides in a row.',
    '- Use at least FOUR different structures across the deck; if the deck is shorter than four slides, give every slide a different one.',
    `- ${NO_LIST_RULE}`,
  ]
  // The style's own ordering is FOLDED IN rather than emitted twice: `layoutLines` is withheld whenever this
  // section runs, because naming a pattern in two places is the double-statement this file's header warns a
  // model reads as emphasis — and here it would emphasise a subset over the menu it was just handed.
  const preferred = style.layout.patterns.filter((pattern) => PATTERN_RULE[pattern] !== undefined)
  if (preferred.length > 0) {
    lines.push(
      `This style reaches for ${preferred.join(', ')} first — prefer ${preferred.length === 1 ? 'it' : 'them'} where the content allows, without breaking the three rules above.`,
    )
  }
  return lines
}

/**
 * The PLANNED section: the decided shape of each slide, verbatim.
 *
 * Order is the plan's own, never re-sorted — the plan is an instruction, and a renderer that silently
 * reordered it would be making the shape decision it exists to record.
 *
 * The "never twice running / four distinct" rules are NOT restated here. The plan has already decided both,
 * and an instruction that contradicts the plan gets one of the two ignored, unpredictably which. The list ban
 * survives, because a model can render a perfectly-named LEDGER as five bullets and think it complied.
 */
function plannedLines(plan: LayoutPlanEntry[]): string[] {
  const entries = plan.filter((entry) => PATTERN_RULE[entry.pattern] !== undefined)
  if (entries.length === 0) return []

  // First-use order, deduped: nine descriptions for a three-pattern plan is eight lines of menu for a deck
  // that is no longer choosing from one.
  const used: LayoutPattern[] = []
  for (const entry of entries) if (!used.includes(entry.pattern)) used.push(entry.pattern)

  return [
    '',
    '## Layout — each slide already has its shape',
    "Build every slide to the structure named for it. The shape was chosen from that slide's content, so do not choose another and do not reorder them.",
    ...entries.map((entry) => {
      // A trailing full stop in the reason would double the one this line adds. Newlines are collapsed
      // because a reason is written by a model and one line break here would split a slide's instruction into
      // two unrelated-looking lines. An empty reason drops the clause rather than emitting "because ." — the
      // shape still stands without its justification.
      const why = entry.why.trim().replace(/\s+/g, ' ').replace(/\.+$/, '')
      // The beat comes FIRST, before the shape: the slide is about this one idea, and the structure is how
      // that idea is made legible. Stating the shape alone let the model keep its own content.
      const carries = (entry.carries ?? '').trim().replace(/\s+/g, ' ').replace(/\.+$/, '')
      const head = carries ? `- Slide ${entry.slide} carries "${carries}" — ` : `- Slide ${entry.slide} — `
      return `${head}${entry.pattern.toUpperCase()}${why ? `, because ${why}` : ''}.`
    }),
    'What those names mean:',
    ...used.map((pattern) => `- ${PATTERN_RULE[pattern]}`),
    NO_LIST_RULE,
  ]
}

function layoutInstructions(style: Style, mode: StyleLabLayoutMode, plan: LayoutPlanEntry[] | undefined): string[] {
  if (mode === 'off') return []
  if (mode === 'planned') {
    const lines = plannedLines(plan ?? [])
    // A planned run whose plan never arrived must not be SCORED as the floor, and must not silently BE the
    // floor either. The vocabulary is the strict subset of what a plan says, so it degrades to that instead of
    // to silence — and which happened stays readable, because the session records `config.layoutPlan` beside
    // the mode.
    return lines.length > 0 ? lines : vocabularyLines(style)
  }
  return vocabularyLines(style)
}

/**
 * The slide-generation block: a complete `## Brand style` section for the flow system prompt, plus the
 * `## Layout` section when a layout mode asks for one.
 *
 * `strength` (0–100) is tiered exactly as the live pipeline tiers it (`styleStrengthTier`): the FACTS never
 * change, only how forcefully the model is told to adopt them, because this is a text-to-slide generator
 * with no diffusion strength knob. At 0 the brand block disappears entirely.
 *
 * Absent fields emit NOTHING. A line reading "spacing: unspecified" is worse than silence — it spends
 * attention naming a thing the model then has to invent anyway, and it invents it differently each run.
 *
 * WHY A LAYOUT SECTION EXISTS AT ALL. Measured 2026-08-13, 5 brands × 20 generations: `layout.patterns` was
 * `[]` for every source, `space` was `{}` for four of five, and the px values across all twenty results were
 * the same handful — 24, 120, 64, 48, 16. Colour and logo varied; structure and rhythm did not. The fields
 * that would have carried them were empty, so the shared system prompt answered those questions, identically,
 * every time. The numbers below (spacing step, fill budget, radius set, shadow) have always been emitted here;
 * what was missing was anything at all about SHAPE, and on the live `tokens` channel even the numbers never
 * arrive, because `BrandStyleTokens` has no field for them (see {@link styleToBrandStyleTokens}).
 *
 * LAYOUT IS NOT TIERED BY `strength`. That dial governs the style — how much of a brand's dress this deck
 * wears. What shape an argument takes is not part of the dress, and tiering them together would mean a
 * strength-0 run silently drops the layout treatment, so the experiment's floor and its treatment would
 * differ by two variables instead of one. `layoutMode` is the layout's own dial, and `off` is its zero.
 */
export function styleToPromptBlock(style: Style, opts?: number | StylePromptOptions): string {
  const { strength, layoutMode = 'off', plan } = promptOptions(opts)
  const layout = layoutInstructions(style, layoutMode, plan)
  const tier = styleStrengthTier(strength)
  // `[].join('\n')` is '', so mode `off` at strength 0 returns the empty string this has always returned.
  if (tier === 'off') return layout.join('\n')

  const facts = [
    ...colorLines(style),
    ...typeLines(style),
    ...spaceLines(style),
    ...radiusLines(style),
    ...shadowLines(style),
    // Withheld while a LAYOUT section runs — that section states the same patterns and folds this ordering in.
    ...(layout.length > 0 ? [] : layoutLines(style)),
    ...assetLines(style),
  ]
  // Rules ride here too. The recipe is the artifact path's door and this is the deck-flow path's, and a rule
  // that only holds on one of them is a rule the user watches come back on the other.
  const rules = ruleLines(style)
  const voice = [...(rules.length ? ['', ...rules] : []), ...voiceLines(style, tier)]
  // `buildBrandStyleBlock` bails when palette AND roles are empty, because colour and type were the only
  // things it could say. A Style also carries layout patterns and voice — exactly what a moodboard read off
  // blurry references produces — so the bail here is "nothing to say at all", not "no colours".
  if (facts.length === 0 && voice.length === 0) return layout.join('\n')

  const framing = strengthFraming(tier)
  // The "use that number" promise only holds where the framing asks for adoption; at light/faint the whole
  // point is that the model may deviate, and an instruction that contradicts the framing gets one of them
  // ignored — unpredictably which.
  const precision =
    tier === 'strict' || tier === 'close'
      ? ` Where a number is given below, use that number: these are measured values, not suggestions.`
      : ''
  // Layout goes AFTER the brand footer, as its own section, because that footer summarises the brand and a
  // section wedged between the two would orphan it. Structure is not brand; it reads as its own heading.
  return [
    '',
    '---',
    '',
    framing.header,
    `${framing.intro}${precision}`,
    ...facts,
    ...voice,
    framing.footer,
    ...layout,
  ].join('\n')
}

/** The palette entry a designer would call the accent: the most prominent colour not already doing a
 *  structural job. Only consulted when the source never named an accent — palette order is "most prominent
 *  first", so this reads the data rather than inventing a colour. */
function paletteAccent(style: Style): string | undefined {
  const taken = new Set(
    [style.color.bg, style.color.surface, style.color.text, style.color.muted, style.color.border].filter(
      (c): c is string => !!c,
    ),
  )
  return style.color.palette.find((c) => isHex(c) && !taken.has(c))
}

/** One sRGB channel, linearized for the WCAG relative-luminance formula. */
function wcagChannel(value: number): number {
  const c = value / 255
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

/**
 * WCAG contrast ratio between two hexes, 1 (identical) to 21 (black on white). `undefined` when either
 * colour will not parse — an unreadable input is not evidence of a bad ratio.
 *
 * WHY THE FORMULA IS HERE. The codebase holds this same eight-line arithmetic twice already
 * (`utils/brandLogoContrast.ts`, `ai/deckLint.ts`) and neither exports it, so there is nothing to import.
 * The PARSE is reused rather than re-typed, which is the half that actually has edge cases.
 */
function contrastRatio(a: string, b: string): number | undefined {
  const ca = parseHex(a)
  const cb = parseHex(b)
  if (!ca || !cb) return undefined
  const la = 0.2126 * wcagChannel(ca[0]) + 0.7152 * wcagChannel(ca[1]) + 0.0722 * wcagChannel(ca[2])
  const lb = 0.2126 * wcagChannel(cb[0]) + 0.7152 * wcagChannel(cb[1]) + 0.0722 * wcagChannel(cb[2])
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * The floor a colour role has to clear before an artifact is allowed to paint text with it.
 *
 * MEASURED, 2026-08-13, granola.ai: the scrape assigned `text: #ffdef6` against `bg: #ffffff` — pale pink on
 * white, a ratio of 1.23:1, unreadable. Slides survived it because the flow model reads the brand block as
 * prose and quietly substituted black. Artifacts do NOT: `--a-text` goes into `:root` and every heading and
 * every line of body copy is `var(--a-text)`, literally. That difference is the whole reason colour breaks on
 * artifacts and not on slides.
 *
 * 3:1 rather than 4.5:1 because the contract's own type scale (body 32-44px, headings 80-200px) is squarely
 * inside WCAG's large-text band, where 3:1 is the AA threshold. It is a floor, not a target: a designed grey
 * such as #808080 on white measures 3.95 and survives, granola's muted #72726e measures 4.83 and survives,
 * and the pink fails by a factor of two and a half. Nothing near the line is being taken away.
 */
const ARTIFACT_TEXT_MIN_CONTRAST = 3

/** A text-carrying role withheld because it cannot be read on the ground it would have sat on. Carried out
 *  of the token pass so the recipe can say WHY the token is missing instead of pretending it never existed. */
interface UnreadableRole {
  /** Full custom-property name, e.g. `--a-text`. */
  property: string
  value: string
  ground: string
  /** WCAG ratio, measured. */
  ratio: number
}

interface ArtifactVars {
  vars: Record<string, string>
  unreadable: UnreadableRole[]
}

/**
 * The token pass, shared by {@link styleToArtifactVars} and the theme recipe so the prose can never describe
 * a token the document did not receive.
 *
 * EVERY EMITTED VALUE IS ONE THE DOCUMENT CAN USE AS-IS. Colours go through `normalizeHex`, which is the
 * invariant `StyleColor` already claims ("always hex, always 6-digit — importers normalise") and which also
 * absorbs `#abc` and `rgb(...)` from a hand-edited style. A colour that will not normalise is DROPPED rather
 * than passed through: it would land raw inside `:root`, where a stray `;` or `}` ends the block early, and
 * it is also a colour this file cannot check for contrast — an unchecked value in `--a-text` is exactly the
 * failure below.
 */
function artifactVars(style: Style): ArtifactVars {
  const vars: Record<string, string> = {}
  const unreadable: UnreadableRole[] = []

  const putColor = (name: string, value: string | undefined): string | undefined => {
    const hex = normalizeHex(value)
    if (hex) vars[name] = hex
    return hex
  }
  const put = (name: string, value: string | undefined) => {
    const trimmed = value?.trim()
    if (trimmed) vars[name] = trimmed
  }

  const bg = putColor('--a-bg', style.color.bg)
  const surface = putColor('--a-surface', style.color.surface)
  putColor('--a-text', style.color.text)
  putColor('--a-muted', style.color.muted)
  putColor('--a-accent', style.color.accent ?? paletteAccent(style))
  putColor('--a-border', style.color.border)
  put('--a-font-display', fontStack(style.type.display))
  put('--a-font-body', fontStack(style.type.body))

  // THE CONTRAST GUARD. Judged against `--a-bg` — the contract calls it "the ground under the whole artifact"
  // and the recipe's own colour prose says nothing else paints a large flat area — falling back to
  // `--a-surface` only when a style names cards but no ground. WITHHELD, never substituted: the record's
  // rule is that no role stands in for another, and a text colour we invented is a slide that no longer
  // looks like the brand. The recipe turns the withheld token into an instruction to choose one, which is
  // the only place the choice can be seen.
  const ground = bg ?? surface
  if (ground) {
    for (const property of ['--a-text', '--a-muted'] as const) {
      const value = vars[property]
      if (!value) continue
      const ratio = contrastRatio(value, ground)
      if (ratio == null || ratio >= ARTIFACT_TEXT_MIN_CONTRAST) continue
      delete vars[property]
      unreadable.push({ property, value, ground, ratio })
    }
  }

  // One radius token, so the scale collapses to its most-used step. `0` survives: a square corner is a design
  // position, not a missing value.
  const radius = firstNumber([style.radius.md, style.radius.lg, style.radius.sm])
  if (radius != null && radius >= 0) vars['--a-radius'] = `${radius}px`
  const unit = style.space.unit
  if (unit != null && Number.isFinite(unit) && unit > 0) vars['--a-space'] = `${unit}px`

  return { vars, unreadable }
}

/**
 * Style → the `--a-*` custom properties an artifact declares.
 *
 * THIS IS THE SEAM THE AUDIT FOUND MISSING. Artifacts (birtvyOpen, Contract v2) declare their tokens in a
 * `:root` block — at minimum `--a-bg`, `--a-text`, `--a-accent`, `--a-muted`, `--a-font-display`,
 * `--a-font-body`, `--a-radius` (artifactContract.ts, TOKENS), with further `--a-*` properties welcome — and
 * `swapTokens` re-themes a finished document by rewriting exactly those values, no model call. Brand kits
 * speak `roles.background` / `roles.text` / `roles.accent`. Nothing translated, so a brand kit could not
 * reach an artifact at all. Pair this with `styleFromArtifactTokens` and the loop closes.
 *
 * The result is keyed by full property name, so it feeds `swapTokens(html, vars)` directly. `--a-surface`,
 * `--a-border` and `--a-space` are emitted past the required seven because a document that declares them
 * should get them, and `swapTokens` inserts nothing for a property the document never declared — an unknown
 * key is free, a missing one is a half-themed slide.
 *
 * A key is OMITTED rather than emitted empty, and no role stands in for another. Both follow from the same
 * fact: an absent key leaves the document's own authored value standing, which is a better slide than a
 * borrowed one. Aliasing surface onto bg would tint the ground and the cards the same colour, and inventing
 * a text colour risks an unreadable slide. Filling real gaps belongs to `styleGaps` + a preset, one layer up
 * where it can be seen. The single exception is the accent, which is a READING of the ordered palette rather
 * than a new value.
 *
 * A text role that cannot be READ on its own ground is withheld too, for the same reason and on a measured
 * one — see {@link ARTIFACT_TEXT_MIN_CONTRAST}.
 */
export function styleToArtifactVars(style: Style): Record<string, string> {
  return artifactVars(style).vars
}

/**
 * THE ONE BUDGET. `/birtvy-open-api` clamps `themeRecipe` at this many characters and appends a truncation
 * note past it; the route imports this constant rather than restating it, so the two cannot drift. Building
 * to the ceiling here means the recipe is cut at a SECTION boundary instead of mid-instruction: a recipe that
 * stops halfway through "never set another font-weight on it" is worse than one that never said it.
 *
 * Callers that append text AFTER the recipe (the artifact resolver's precedence line, the kit page's
 * hit-testing law) declare it as `ThemeRecipeOptions.reserve`, and the fit runs against the remainder.
 * Measured 2026-09-05: without that, a rich kit's recipe filled the 8,000 and the 1,563 characters of stage
 * laws appended behind it pushed the type sizes past the route's slice, mid-sentence.
 */
export const MAX_THEME_RECIPE = 8_000

/** Free art direction has no length bound (a moodboard synthesis writes paragraphs). Clamped so one prose
 *  field cannot crowd out the token block and the type rules, which are the parts nothing else states.
 *  800, down from 1,200 (2026-09-05): it is also the first section the fit drops, so a long guideline was
 *  costing itself whole anyway. */
const MAX_RECIPE_GUIDELINE = 800

/**
 * The standing-rules budget, about eight lines.
 *
 * Sized against {@link MAX_THEME_RECIPE} rather than guessed (re-done 2026-09-05 for the five size lines and
 * the lock): system + tokens + colour + type run about 3.2k, density/shape/depth 0.5k, voice 0.6k, logo
 * 0.6k, the guideline 0.8k, which leaves this much before `fitRecipe` has to drop anything on a
 * fully-filled kit with a 700-character reserve behind it.
 *
 * Overflow is stated, never silent. A user who wrote twenty rules and sees eight arrive is owed the count,
 * because the alternative is a kit that quietly stops enforcing what it was told.
 */
const MAX_RECIPE_RULES = 1_000

/** The seven every artifact must declare (artifactContract.ts, TOKENS). A Style that cannot fill one has to
 *  say so out loud: a token block that silently omits `--a-accent` reads as a complete list, and the model
 *  then either invents an accent it never declares in `:root` — a contract violation the validator rejects —
 *  or drops the accent altogether. */
const REQUIRED_ARTIFACT_TOKENS = [
  '--a-bg',
  '--a-text',
  '--a-accent',
  '--a-muted',
  '--a-font-display',
  '--a-font-body',
  '--a-radius',
] as const

/**
 * How forcefully the document is told to adopt the theme.
 *
 * The route wraps whatever it receives in "This recipe owns palette, fonts, weights, density and voice, and
 * it must be followed exactly", which at the loose tiers directly contradicts the recipe's own content — so
 * those tiers state what following them exactly MEANS. Tiers are the product's (`styleStrengthTier`), never a
 * second scale invented here.
 */
const RECIPE_FRAMING: Record<Exclude<StyleStrengthTier, 'off'>, string> = {
  strict: "This recipe is the document's entire aesthetic. Use these exact values and invent nothing outside them.",
  close:
    'Follow this closely: the document should clearly read as this theme. Adjust only where legibility or content fit demand it.',
  light:
    'Light inspiration only: take the mood, one or two of the colours and the general typographic feel, then deviate freely for content fit and legibility. Following this recipe exactly means honouring that latitude.',
  faint:
    'A faint nod only: perhaps one colour, or a hint of the mood. A clean, legible document beats matching this. Following this recipe exactly means honouring that latitude.',
}

/** Content padding by density, inside the contract's own 100-160px band, and landing on the two values its
 *  worked examples use (120 → 840 usable, 160 → 760). The contract makes the whole vertical budget depend on
 *  this one number, so naming it is what turns "airy" into arithmetic the model can check itself against. */
const DENSITY_PADDING: Record<StyleDensity, number> = {
  tight: 100,
  normal: 120,
  airy: 160,
}

/** Palette entries not already doing a role's job. Offered as further `--a-*` properties, which is exactly
 *  what the contract invites ("More custom properties are welcome, always prefixed --a-"). */
function unusedPaletteHexes(style: Style): string[] {
  const taken = new Set(
    [
      style.color.bg,
      style.color.surface,
      style.color.text,
      style.color.muted,
      style.color.accent ?? paletteAccent(style),
      style.color.border,
    ].filter((c): c is string => !!c),
  )
  return style.color.palette.filter((c) => isHex(c) && !taken.has(c))
}

/** How the roles are USED, never what they are. The token block already states every value, and a recipe that
 *  repeats a hex in prose spends the model's attention twice on one fact. The accent line is the one that
 *  earns its place: flooding a slide with the brand colour is the commonest way a generated document stops
 *  looking like the brand.
 *
 *  Keyed off the EMITTED tokens rather than off the Style, so a role the token pass withheld is never
 *  described here as though the document had it. */
function recipeColorLines(
  style: Style,
  vars: Record<string, string>,
  /** Tokens the Tokens section just asked the model to pick. The closing rule must not forbid them. */
  invented: readonly string[],
  tier: StyleStrengthTier,
): string[] {
  const lines: string[] = []
  if (vars['--a-bg'])
    lines.push('- var(--a-bg) is the ground under the whole artifact. Nothing else paints a large flat area.')
  if (vars['--a-surface']) lines.push('- var(--a-surface) is for the cards and panels sitting on that ground.')
  if (vars['--a-text']) lines.push('- var(--a-text) carries every heading and every line of body copy.')
  if (vars['--a-muted']) lines.push('- var(--a-muted) is for labels, captions and anything deliberately secondary.')
  if (vars['--a-accent'])
    lines.push(
      '- var(--a-accent) is a seasoning: one number, one word, a rule, a small mark. Never a whole field of it.',
    )
  if (vars['--a-border']) lines.push('- var(--a-border) draws hairlines and dividers, never a heavy frame.')
  // Bounded: a website scrape can hand back a hundred incidental hexes, and a list that long is noise a model
  // cannot hold anyway — while inside a fixed recipe budget it is noise that crowds out the type rules.
  // Palette order is "most prominent first", so the head of the list is the part worth offering.
  const rest = unusedPaletteHexes(style).slice(0, 8)
  if (rest.length)
    lines.push(
      `- The rest of the palette you may draw from: ${rest.join('  ')}. Declare any you use as further --a-* properties in the same :root block.`,
    )
  // THE OTHER GROUNDS, named so the writer knows they exist and knows this document is not on them. A
  // kit with a white and a navy background used to reach here as one `--a-bg` and a navy swatch in the
  // palette list above, and the swatch is an invitation: the model paints a navy panel across half the
  // artifact and calls it the brand. A ground is a per-document decision (the variants of Phase C key on
  // it), never a fill inside one. Listed WITHOUT the ground this document sits on, so the line never
  // contradicts the token block.
  const others = (style.color.grounds ?? []).filter((g) => isHex(g) && g !== vars['--a-bg'])
  if ((style.color.grounds?.length ?? 0) > 1 && others.length > 0)
    lines.push(
      `- This brand has other grounds (${others.join(', ')}). Each document sits on ONE; this one sits on var(--a-bg). Never paint a second ground inside it.`,
    )
  // THE CLOSING RULE, and the section is wrong without it. The slide path has carried
  // "Do not invent colors outside this palette" since the strict tier was written; the artifact recipe
  // never said it, and ended instead on the open invitation above — a list of hexes, an instruction to
  // declare more --a-* properties, and no sentence anywhere saying the palette is the whole world. An
  // artifact is a complete HTML document, so an invented hex is not a suggestion a later pass tidies up:
  // it ships literally, which is exactly why the slide path's silent substitutions never hid this.
  // Guri, 2026-09-03: "every color which is added is meaningfully used in the composition part" — the
  // other half of that is that no colour which was NOT added gets used at all.
  //
  // Kept separate from the --a-* sentence above on purpose: that one is MECHANICS (where to declare a
  // colour you use), this one is PERMISSION (which colours exist). Folding them into one line has the
  // permission rule read as a footnote to a formatting rule.
  /* Two guards, both found by reading the assembled prompt rather than the diff.
     TIER, matching the slide path this rule is borrowed from: at the loose tiers the lead already says
     "These are reference values, not a requirement. Deviate where you like", and closing with "do not
     invent colors" makes one recipe argue with itself. Production artifacts run strict, so this only
     spares the lab, but the lab is where the tiers get tuned.
     THE INVITED TOKENS: when the Tokens section has just said "this theme sets no --a-muted, choose
     that one yourself", a flat ban leaves the model no legal way to obey. Naming the exemption keeps
     both sentences true at once. Withheld roles come through `invented` too, so the branch that says
     "--a-text is deliberately absent" no longer leaves the document without any legal ink. */
  if (lines.length > 0 && (tier === 'strict' || tier === 'close')) {
    lines.push(
      invented.length > 0
        ? `- Do not invent colors outside this palette, apart from the ${invented.join(', ')} above that this theme asked you to choose.`
        : '- Do not invent colors outside this palette.',
    )
  }
  return lines
}

/**
 * Type as the contract talks about type: families behind their tokens, the weights the files actually ship,
 * and — when the record carries the kit's five roles — THE FIVE SIZES, as law.
 *
 * Measured 2026-09-05: this section emitted families, weights and a ratio, never a px, and the sizes rode a
 * separate block appended after the budget fit that a rich kit cut mid-sentence. Guri: "the font size that
 * is there as a title should match the display". So with `type.roles` present the ratio line goes (a ratio
 * is a way of saying sizes without saying them) and the role lines carry each size with its weight, leading,
 * tracking and case, largest first, closing on the rule that makes it a system: five sizes and no sixth.
 * Without roles the output is byte for byte what it was, so a wired site with no scale loses nothing.
 */
/**
 * The five roles in the CONTRACT's words, not the panel's. The Typography panel says Paragraph and Small
 * paragraph because that is the ladder Guri pointed at; the contract's bands (`artifactContract.ts`) say
 * Body and Caption, and the writer reads both in one prompt. One name per role, or the model is left to
 * work out that "Paragraph 32px" is the thing the bands call Body.
 */
const RECIPE_ROLE_NAMES: Record<TypeRole, string> = {
  display: 'Display',
  heading: 'Heading',
  subheading: 'Subheading',
  body: 'Body',
  caption: 'Caption',
}

function recipeTypeLines(style: Style): string[] {
  const { display, body, displayWeights, scale, tracking, leading, roles } = style.type
  const lines: string[] = []

  const shipped = (displayWeights ?? []).filter((w) => /^\d{3}$/.test(w)).sort((a, b) => Number(a) - Number(b))
  if (roles) {
    // The family line and its traits are withheld: every role line names its own token and carries its
    // own weight, leading, tracking and case, so "Headings use var(--a-font-display), and set line-height
    // 1.1 on every one of them" after "Heading 96px, line-height 1.1, var(--a-font-display)" is the double
    // statement this file's header warns a model reads as emphasis — and 60 characters of budget.
    lines.push(...roleLines(roles))
  } else {
    const traits: string[] = []
    if (shipped.length > 0) traits.push(`font-weight:${shipped[shipped.length - 1]}`)
    if (style.type.case === 'upper') traits.push('text-transform:uppercase')
    const letterSpacing = safeTracking(tracking)
    if (letterSpacing) traits.push(`letter-spacing:${letterSpacing}`)
    if (leading != null && Number.isFinite(leading)) traits.push(`line-height:${leading}`)

    if (display || traits.length > 0) {
      const suffix = traits.length > 0 ? `, and set ${traits.join('; ')} on every one of them` : ''
      lines.push(`- Headings and the hero title use var(--a-font-display)${suffix}.`)
    }
  }
  if (shipped.length > 0) {
    // The same faux-bold guard the slide block carries, and it bites harder here: an artifact sets its hero
    // title at 140-200px, where a synthesized bold is not a subtle thickening but a visibly broken face.
    lines.push(
      `- The display face ships ONLY weight${shipped.length === 1 ? '' : 's'} ${shipped.join(', ')}. Never set another weight on it: the browser fakes the bold and the letterforms crush at hero size.`,
    )
  }
  if (body) lines.push('- Body copy and captions use var(--a-font-body).')

  const families = [display, body].filter((f): f is string => !!f)
  const unique = [...new Set(families)]
  if (unique.length > 0) {
    lines.push(
      `- Load ${unique.join(' and ')} with a Google Fonts <link> in the head, naming only the weights above. If a family is not on Google Fonts, keep it first in the token's stack and let the fallback carry it.`,
    )
  }
  if (roles) {
    lines.push(
      '- These five are the only font-sizes in the document. Never an intermediate size for emphasis: change weight, colour or role instead.',
    )
  } else if (scale != null && Number.isFinite(scale) && scale > 1) {
    lines.push(
      `- This theme steps by ×${scale} between type levels. Hold that ratio between the sizes you pick inside the contract's bands, and never pick a size between two steps.`,
    )
  }
  return lines
}

/**
 * One line per role, largest first: `- Display 160px, weight 700, line-height 1, tracking -0.02em,
 * UPPERCASE, var(--a-font-display). The cover title, and nothing else.` Tracking, leading and case only
 * when the scale sets them — a line that says "tracking 0em" spends attention on a non-decision — and the
 * usage rule last, because it is the half a model gets wrong on its own (a caption in the display face).
 * Tracking is stated VERBATIM, not through `safeTracking`: that clamp exists for a site's 16px-tuned
 * tracking landing on a 120px title, and a role's tracking was set for that role's own size.
 */
function roleLines(roles: TypeScale): string[] {
  return TYPE_ROLES.filter((role) => roles[role]).map((role) => {
    const s = roles[role]
    const bits = [
      `${RECIPE_ROLE_NAMES[role]} ${s.size}px`,
      `weight ${s.weight}`,
      s.leading != null && Number.isFinite(s.leading) ? `line-height ${s.leading}` : '',
      s.tracking != null && Number.isFinite(s.tracking) ? `tracking ${s.tracking}em` : '',
      s.case === 'upper' ? 'UPPERCASE' : '',
      `var(--a-font-${s.face === 'display' ? 'display' : 'body'})`,
    ].filter((b) => b !== '')
    const usage = s.usage?.trim()
    return `- ${bits.join(', ')}.${usage ? ` ${usage}` : ''}`
  })
}

/** Density expressed as the contract's own arithmetic: one padding value, the usable height it produces, the
 *  share of it to fill, and gaps as whole multiples of the spacing step. */
function recipeSpaceLines(style: Style): string[] {
  const lines: string[] = []
  const density = style.space.density
  const unit = style.space.unit
  const hasUnit = unit != null && Number.isFinite(unit) && unit > 0

  if (density) {
    const padding = DENSITY_PADDING[density]
    lines.push(
      `- Content padding ${padding}px from every edge, one value held across the whole artifact, so the usable height is ${1080 - 2 * padding}px.`,
    )
    const fill = DENSITY_FILL[density]
    lines.push(`- ${fill.charAt(0).toUpperCase()}${fill.slice(1)}. ${DENSITY_RULE[density]}`)
  }
  if (hasUnit) {
    const steps = [1, 2, 3, 4, 6, 8].map((m) => `${m * unit}`).join(', ')
    lines.push(
      `- Spacing step ${unit}px: every gap and every padding is a whole multiple of it (${steps}). Never a value off that step.`,
    )
    if (density) {
      const [lo, hi] = DENSITY_STEPS[density]
      lines.push(`- Gaps of ${lo * unit} to ${hi * unit}px between blocks.`)
    }
  }
  return lines
}

/** Depth, with the measured values routed through a token. A literal `rgba(...)` in a box-shadow is a raw
 *  colour outside `:root`, which the contract calls a violation, so the shadow has to be named before it can
 *  be used. */
function recipeDepthLines(style: Style): string[] {
  const lines: string[] = []
  if (style.shadow.depth) lines.push(`- ${DEPTH_RULE[style.shadow.depth]}`)
  const measured = (style.shadow.values ?? []).map((v) => v.trim()).filter((v) => v !== '')
  if (measured.length) {
    lines.push(
      `- Use exactly these shadow values, declared as --a-shadow properties in the same :root block and reached through var(): ${measured.join(' | ')}.`,
    )
  }
  return lines
}

/**
 * Corner radius as a RULE, not just a token.
 *
 * `--a-radius` alone states one number and says nothing about where it goes, so a document declares it and
 * then rounds one card and squares the next. The second line exists because the token pass collapses the
 * whole scale to a single step (see {@link styleToArtifactVars}): a brand that measured 4 / 16 / 32 loses two
 * thirds of its shape language unless the other two are handed over as further --a-* properties, which is
 * exactly what the contract invites.
 */
function recipeRadiusLines(style: Style, vars: Record<string, string>): string[] {
  const radius = vars['--a-radius']
  if (!radius) return []
  const lines = [
    `- Corner radius: var(--a-radius) on every card, panel, chip and image. One shape, held across the document, never another value.`,
  ]
  const rest = [style.radius.sm, style.radius.md, style.radius.lg]
    .filter((v): v is number => v != null && Number.isFinite(v) && v >= 0)
    .filter((v) => `${v}px` !== radius)
  const unique = [...new Set(rest)].sort((a, b) => a - b)
  if (unique.length > 0) {
    lines.push(
      `- This theme also measures ${unique.map((v) => `${v}px`).join(' and ')}. Declare ${unique.length === 1 ? 'it' : 'them'} as further --a-radius-* properties in the same :root block and use the small one on chips and labels, the large one on full panels.`,
    )
  }
  if (radius === '0px') {
    lines.push('- A square corner is the position this theme takes. Do not soften it anywhere.')
  }
  return lines
}

/**
 * THE MARK. Measured complaint (Guri, 2026-08-13): artifacts arrive with no logo at all.
 *
 * The slide path has carried a logo line for a long time; the recipe never did, and the recipe is the
 * artifact's only door. So the mark is stated here, with the two things that decide whether it lands: the
 * placement (once, in flow, at the head of the content column) and the CONTRAST — `assets.logoOnLight` is a
 * fact the kit measured about the FILE, and a light mark on a light ground is a mark that is technically
 * present and visually absent.
 *
 * THE EXEMPTION IS STATED OUT LOUD. The contract's SELF-CONTAINED rule bans remote images, and its LOGOS
 * carve-out has the model declare `data-logo` with no src precisely because a model composing a logo URL
 * gets a rival's trademark (artifactLogos.ts). Neither applies to a mark the deck ALREADY HOLDS: this URL was
 * not composed by the model, it came off the brand's own page. Left unsaid, the system prompt wins and the
 * mark is dropped, which is exactly what has been happening.
 */
function recipeLogoLines(style: Style, vars: Record<string, string>, assets: SiteAssets | undefined): string[] {
  // The Style wins: it is what a merge by confidence settled on and what a user edit lands in. The harvest is
  // the fallback for the case that produced the complaint — a lab run with a site but no saved kit, where
  // `SiteAssets.logoUrl` is the only mark anyone has.
  const logo = style.assets.logoUrl?.trim() || assets?.logoUrl?.trim()
  if (!logo) return []
  const name = style.name.trim() || 'the brand'
  const lines = [
    `- The mark is a real file the deck already holds: ${logo}`,
    // HEIGHT ONLY, and never a fixed width. A 140x44 box is a WORDMARK's shape, and a mark that is
    // roughly square inside it renders correctly and looks broken: contained and pinned left, it draws
    // at 48px in a 140px box and leaves 90px of dead plate to its right, which is what the writer then
    // sizes its background to. Guri, 2026-09-04, on a logo that was finally loading: "look still weirdly
    // loads". Letting the width follow the mark's own aspect makes one instruction correct for a wide
    // wordmark and a square glyph alike, and drops the need for object-fit and object-position at all.
    // Kept to ONE line: this recipe is budgeted, and an earlier two-line version of this rule pushed it
    // past the cap so `fitRecipe` silently dropped the whole Site material section.
    // `flex:none;align-self:flex-start` because the writer's first instinct is a column flex box, and
    // flex stretches a replaced element to the column: measured 2026-09-05, a 24x22 mark in a 640x44 box,
    // drawn at its centre. The bytes pass is what guarantees the box (`markGeometry`); this makes the
    // snippet itself correct so the tile the writer sees while it works is not lying to it.
    `- Place the FILE AS UPLOADED, exactly ONCE, small, as the FIRST element of the content column, in normal flow, never absolutely positioned, never inside a plate, box or pill, never on a background you add, and never recoloured, cropped or redrawn: <img src="${logo}" alt="${name}" style="height:44px;width:auto;display:block;flex:none;align-self:flex-start">.`,
  ]

  // `logoOnLight` means the mark reads ON a light background, so a mark that reads on light is dark ink.
  // The decision is the product's own (`markReadsOnBackground`), not a second opinion invented here.
  const onLight = style.assets.logoOnLight
  const bg = vars['--a-bg']
  if (onLight == null) {
    lines.push(
      '- Which ground this mark reads on was never measured, so give it clear space on var(--a-bg) and never sit it on the accent, on a photograph or on a busy field.',
    )
  } else if (!bg) {
    lines.push(
      `- The mark reads on a ${onLight ? 'LIGHT' : 'DARK'} ground, so put it where the document is already ${onLight ? 'light' : 'dark'}. Do not give it a ground of its own.`,
    )
  } else if (markReadsOnBackground(bg, onLight)) {
    lines.push(
      `- The mark reads on a ${onLight ? 'LIGHT' : 'DARK'} ground and var(--a-bg) is one, so it sits straight on the ground with clear space around it.`,
    )
  } else {
    // NEVER A PLATE. This branch used to say "sit it on its own small plate: a dark block with
    // var(--a-radius) corners", and writers obeyed it exactly — Guri got a near-square mark stranded in a
    // wide dark pill on a light slide. It also misreads what the kit stores. Guri, 2026-09-04: "I did not
    // say that we needed to have a background color for the logo. Those logo variants were: if this logo
    // ever surfaces, those are the kinds of colors." A variation is the mark MADE FOR a ground, not a mark
    // wearing one, so the answer to a tone mismatch is the other variation, never a swatch drawn behind
    // this one. `withStoredMark` picks by the theme's own background, so reaching here means the kit has
    // no variation for this ground — which the user can fix in one click, and a plate would hide.
    lines.push(
      `- The mark reads on a ${onLight ? 'LIGHT' : 'DARK'} ground and var(--a-bg) is the opposite, so put it somewhere the document is already ${onLight ? 'light' : 'dark'}, and if there is nowhere, place it anyway rather than building one.`,
    )
  }

  // The exemption rides the closing line rather than its own (merged 2026-09-05 for the budget): it has
  // to be said out loud or the system prompt's no-remote-images rule wins, and it costs one clause here.
  // THE MARK STANDS ALONE. This line used to say "Keep ${name} readable as text nearby", borrowed from
  // the contract's rule for THIRD-PARTY marks (a declaration the system may fail to verify and remove,
  // so the company's name must survive in text). The kit's own mark is never removed, and the writers
  // took the sentence literally: an eyebrow reading "Deck core" under the Deck core mark on every
  // regeneration (Guri, 2026-09-05: "why it puts that eyebrow?"). The mark IS the name.
  lines.push(
    `- The mark stands alone: never write ${name} as a label, eyebrow or caption beside or under it, and never stretch, recolour or crop it. Nothing else in the layout may depend on the image existing. This one URL is the exception to the contract's no-remote-images rule, because it is a verified asset rather than a URL you composed; never compose another logo URL and never redraw a mark yourself.`,
  )
  return lines
}

/** A second, looser bound on what the recipe will list. `harvestSiteAssets` already caps images at 6 and a
 *  declaration at 200 characters; this exists so a hand-built or future harvest cannot spend the recipe
 *  budget on a contact sheet. Both caps hold, and the tighter one wins. */
const MAX_RECIPE_IMAGES = 8
const MAX_RECIPE_PATTERNS = 8

/** `1200x800`, or one dimension, or nothing. A size the model can see is what stops it planning a full-bleed
 *  around a 300px thumbnail — the same reason `styleSiteAssets` states it in the slide block. */
function imageSize(image: SiteAssets['images'][number]): string {
  if (image.width != null && image.height != null) return `${image.width}x${image.height}`
  if (image.width != null) return `${image.width}px wide`
  if (image.height != null) return `${image.height}px tall`
  return ''
}

/**
 * THE SITE'S OWN MATERIAL, stated as reusable rather than as trivia.
 *
 * Guri's verdict on the current artifact output (2026-08-13): it is missing the site's real images, its logo
 * and its actual visual patterns, and "it's pure HTML, you can reproduce everything". A theme that hands over
 * a palette and no material produces a document that looks like a colour scheme rather than like the brand.
 *
 * WRITTEN FOR THE RECIPE, not lifted from `assetsToPromptBlock`. That function says the same true things in
 * the SLIDE block's voice, with its own `---` rule and its own heading, and it deliberately states no geometry
 * for the mark because the slide block's `assetLines` already does. Here the `## Logo` section states the
 * geometry, so importing that block would put two different logo instructions in one prompt — the
 * double-statement this file's header warns a model reads as emphasis, except that here the two disagree.
 * The DATA is shared; the wording belongs to the channel.
 *
 * The closing ban is what makes offering URLs safe at all: a listed URL is real, an unlisted one is not, and a
 * model that wants a picture it was not given will compose a plausible one — the exact failure the
 * verified-marks pass exists to prevent for logos. Inside a sandboxed iframe a broken image is simply
 * invisible, so nothing downstream would notice.
 */
function recipeAssetLines(assets: SiteAssets | undefined): string[] {
  if (!assets) return []
  const images = assets.images.filter((i) => i.url.trim() !== '').slice(0, MAX_RECIPE_IMAGES)
  const patterns = assets.patterns.filter((p) => p.css.trim() !== '').slice(0, MAX_RECIPE_PATTERNS)
  if (images.length === 0 && patterns.length === 0) return []

  const lines: string[] = [
    "Everything here was read off the brand's own site, so it is real and it is PREFERRED over anything you would invent. It is the exception to the contract's no-remote-images rule for the same reason the mark is: you did not compose any of it.",
  ]
  if (images.length > 0) {
    lines.push('- Images, by exact URL:')
    for (const image of images) {
      const size = imageSize(image)
      lines.push(`  - ${image.role}${size ? `, ${size}` : ''}: ${image.url}`)
    }
    lines.push(
      '- Place one with <img src="one of the URLs above"> or background-image: url("one of the URLs above"), inside a fixed box with object-fit: cover and a real alt. A hero is worth full-bleed or half the frame, a product shot belongs in a frame with var(--a-radius), a texture is a background and never content.',
    )
    lines.push(
      '- Count every image box into the vertical budget, and use one only where it carries the point. A decorative photograph on an artifact that already has a claim on it is an artifact with two subjects.',
    )
  }
  if (patterns.length > 0) {
    lines.push('- CSS this site actually uses. Reuse these declarations verbatim rather than approximating them:')
    for (const pattern of patterns) {
      lines.push(`  - ${pattern.kind}: ${pattern.css.replace(/\s+/g, ' ')}  (${pattern.note})`)
    }
    lines.push('- Any colour inside one of them still goes through a --a-* token, never as a literal in the body.')
  }
  lines.push(
    '- These lists are CLOSED. A URL or a declaration that is not on them does not exist: never invent one, never edit one, never approximate one.',
  )
  return lines
}

/**
 * ONE THEME, SEVERAL DOCUMENTS.
 *
 * The artifact target generates one document today and Guri wants two or three. The failure mode of a shared
 * recipe across several generations is not that they diverge, it is that they CONVERGE: same tokens, same
 * fonts, same density, and the model reaches for the same composition three times. So the recipe says which
 * half is fixed and which half has to move, and {@link artifactAngle} gives each document its own job.
 */
function recipeSetLines(docCount: number | undefined): string[] {
  if (docCount == null || !Number.isFinite(docCount) || docCount < 2) return []
  const n = Math.floor(docCount)
  // Two lines, down from four (2026-09-05). The lead sentence only restated the count and the closing one
  // restated DECK CONTEXT, which every request already carries; what this section is FOR is the split
  // between what holds and what moves, and that is two sentences.
  return [
    `- The tokens, the two fonts, the padding and the density are IDENTICAL across all ${n} documents in this set. A reader moving between them sees one hand.`,
    '- What changes between them is the STRUCTURE and the subject. Two documents in the set must not be built on the same structure, and none of them is a bulleted list.',
  ]
}

/**
 * Style → the artifact THEME RECIPE (`themeRecipe` on `/birtvy-open-api`).
 *
 * THE ARTIFACT'S ONLY DOOR. `/deckgen-flow-api` takes `BrandStyleTokens` and builds its own `## Brand style`
 * block; the artifact route takes free prose and wraps it in one sentence — "This recipe owns palette, fonts,
 * weights, density and voice, and it must be followed exactly". So a brand kit reaches a document exactly as
 * well as this function writes English. That is also why the contract itself carries no aesthetics
 * (artifactContract.ts: structure rules are fixed forever, aesthetic choices "belong to the deck's theme
 * recipe"). Anything the theme knows arrives here or it does not arrive at all.
 *
 * A NAMED TOKEN BEATS A DESCRIBED COLOUR, so {@link styleToArtifactVars} goes in verbatim as a fenced
 * ```tokens block. That fence is the format the product's own theme editor teaches (BirtvyThemeModal) and the
 * one the deterministic re-theme parses, so a recipe written here can be pasted into the canvas and applied
 * to every artifact without a model call.
 *
 * THE PROSE IS WRITTEN IN THE CONTRACT'S VOCABULARY, not a vocabulary of its own: usable height and the
 * padding it comes from, the fill budget, var() everywhere, the manifest's "theme" key. An instruction
 * phrased in words the system prompt already used is one the model can act on without translating first.
 *
 * THE LOGO AND THE SITE'S MATERIAL ARE CARRIED, and that reverses an earlier decision on measured evidence.
 * This function used to drop `assets.logoUrl` because the contract bans remote images. The result is what
 * Guri sees today: artifacts arrive with no mark, no photographs and none of the treatments the site actually
 * ships, and he called the output "really terrible". The contract's ban exists to stop a model INVENTING an
 * image URL (artifactLogos.ts: logo.dev serves a rival's trademark for a guessed label); a URL the deck
 * already holds was not invented, so the recipe names the exemption out loud and bounds it with "an image not
 * listed here does not exist". Remote images do render: the iframe is `sandbox="allow-scripts"` with no
 * `allow-same-origin`, which blocks fetch on an opaque origin but never blocked an `<img>` — the shipped
 * logo-resolution pass fills a real `src` into finished documents already.
 *
 * CONTRADICTED, UNRESOLVED: `brandKitWire.kitMarkUrl` records a 2026-09-04 measurement in the composition
 * stage's own frame where nothing remote loaded — not our storage, not an absolute URL to this app, not an
 * unrelated public CDN — and `inlineKitMark` exists because of it. Both claims cannot hold. Re-measure
 * before either is relied on again, and delete the loser rather than leaving two of these.
 *
 * `strength` tiers exactly as everything else here does — the FACTS never change, only how forcefully the
 * document is told to adopt them. At 0 the recipe disappears entirely and the model commits to an aesthetic
 * of its own, which is precisely what the contract instructs when no THEME CONTEXT arrives.
 *
 * THE BARE NUMBER STILL MEANS STRENGTH, byte for byte: `styleToThemeRecipe(style, 60)` is what
 * `useStyleLabRun` calls today, and a signature change that forces an edit in another module is a change to
 * that module. Same additive move `StylePromptOptions` made for the slide block.
 */
export interface ThemeRecipeOptions {
  /** 0–100, tiered exactly as the live pipeline tiers it. */
  strength?: number
  /** What the harvest found on the brand's own page: real image URLs and real CSS treatments. */
  assets?: SiteAssets
  /** How many documents this one theme is dressing. 2+ adds the set rules; 1 or absent changes nothing. */
  docCount?: number
  /**
   * Characters the CALLER will append after this recipe (a precedence line, the stage laws), so the fit
   * runs against `MAX_THEME_RECIPE - reserve` and the route's slice never lands mid-sentence. 0 or absent
   * fits to the whole ceiling, which is what a caller that appends nothing has always had.
   */
  reserve?: number
  /**
   * Whether to emit the `## System` lock (default true at the strict and close tiers). `false` is for a
   * caller that is deliberately asking for a different system — a lab treatment, a restyle — and would be
   * telling the writer two opposite things otherwise. The loose tiers never emit it: "light inspiration
   * only" and "this system does not move" cannot both be true.
   */
  lock?: boolean
}

/** The one place the two call shapes converge, so nothing below has to know which one arrived. */
function recipeOptions(opts: number | ThemeRecipeOptions | undefined): ThemeRecipeOptions {
  return typeof opts === 'number' ? { strength: opts } : (opts ?? {})
}

/**
 * A recipe section and what it is worth when the budget runs out.
 *
 * `drop` is a PRIORITY, not a position: the section order below is the reading order the model gets, and the
 * two are different questions. Guri's rule decides the numbers — a document with the right colours and no
 * photos beats one with photos and wrong colours — so the harvested material goes first, the mark second, and
 * everything the tokens depend on is never dropped by this pass at all.
 */
interface RecipeSection {
  text: string
  drop: number
}

const DROP_NEVER = 0
const DROP_LOGO = 1
const DROP_MATERIAL = 2
/** The free art direction goes FIRST (2026-09-05). Guri's rule ranks the mark and the photographs behind
 *  the tokens; the guideline is behind both, because everything measurable in it has already been mined
 *  into a token, and what is left is the one section a model can do without and still look like the brand. */
const DROP_GUIDELINE = 3

function section(heading: string, lines: string[], drop = DROP_NEVER): RecipeSection[] {
  if (lines.length === 0) return []
  return [{ text: [heading, ...lines].join('\n'), drop }]
}

/**
 * Sections into a recipe that fits `budget` — {@link MAX_THEME_RECIPE} less whatever the caller reserved.
 *
 * Two passes, and both are needed. The PRIORITY pass drops whole droppable sections, worst-ranked first,
 * until the recipe fits — that is what makes "assets before tokens" true regardless of where the material
 * section sits in the reading order. The IN-ORDER pass behind it is the original safety net: one pathological
 * field (a scrape that returned four hundred colours) must not be able to take the type rules down with it,
 * so a section that still does not fit costs only itself.
 *
 * Whole sections or nothing, never a sentence cut in half: a recipe that stops halfway through "never set
 * another font-weight on it" is worse than one that never said it.
 */
function fitRecipe(sections: RecipeSection[], budget: number): string {
  const cost = (list: RecipeSection[]) => list.reduce((n, s, i) => n + s.text.length + (i > 0 ? 2 : 0), 0)
  const kept = [...sections]
  while (cost(kept) > budget) {
    let worst = -1
    let rank = DROP_NEVER
    for (let i = 0; i < kept.length; i += 1) {
      if (kept[i].drop > rank) {
        rank = kept[i].drop
        worst = i
      }
    }
    if (worst < 0) break
    kept.splice(worst, 1)
  }

  const out: string[] = []
  let length = 0
  for (const entry of kept) {
    const size = entry.text.length + (out.length > 0 ? 2 : 0)
    if (length + size > budget) continue
    out.push(entry.text)
    length += size
  }
  return out.join('\n\n')
}

/**
 * THE LOCK. What holds between documents and between regenerations of one document, and what is free.
 *
 * Guri, 2026-09-05: "When I regenerate a grid… everything looks exactly the same", and in the same breath
 * the sizes, colours and logo "system does not work" — the two complaints are one: nothing told the
 * writer which half is the system and which half is the content, so it held the wrong things and varied
 * the wrong things. Stated once, second after the header, before a single token is named, so every section
 * after it is read as "the system" by construction. The exact wording is the plan's (section 3, "The lock");
 * the composition FAMILY lock is per role and rides `referenceBrief`, not here.
 */
export const SYSTEM_LOCK =
  'SYSTEM LOCKED. The five type sizes, the colour roles and their hexes, the spacing step and padding, the corner radius, the shadow rule and the mark stated in this recipe are this brand\'s system. They do not move between documents and they do not move between regenerations of the same document. CONTENT FREE. Everything else is yours: the words, the imagery, how many items there are, and the arrangement inside the composition you were given. A regeneration changes the arrangement, never the system.'

/** At most this many structures in `## Structure` (2026-09-05, was unbounded). "Best first" past three is
 *  a menu, and a menu is the vocabulary block's job, not the theme's. */
const MAX_RECIPE_STRUCTURES = 3

export function styleToThemeRecipe(style: Style, opts?: number | ThemeRecipeOptions): string {
  const { strength, assets, docCount, reserve, lock } = recipeOptions(opts)
  const tier = styleStrengthTier(strength)
  if (tier === 'off') return ''
  const roles = style.type.roles
  const budget = MAX_THEME_RECIPE - Math.max(0, Number.isFinite(reserve ?? 0) ? (reserve ?? 0) : 0)

  // Reading order: System · Tokens · Colour · Type · Density · Shape · Depth · Voice · Structure · Logo ·
  // Site material · Rules · Art direction · The set · Manifest. `drop` ranks decide what goes when the
  // budget bites, and they are a different question from this order — see `RecipeSection`.
  const body: RecipeSection[] = []

  const { vars, unreadable } = artifactVars(style)
  const tokens = Object.entries(vars)
  /* Hoisted out of the Tokens block because the COLOUR section has to know it too: these are the tokens
     the recipe itself tells the model to choose, and the closing "do not invent colors" rule has to
     exempt them by name or the two sections contradict each other in the same prompt. A withheld role
     lands here as well, since it never reached `vars`. */
  const invented = REQUIRED_ARTIFACT_TOKENS.filter((token) => !(token in vars))
  if (tokens.length > 0) {
    const missing = invented
    const lead =
      tier === 'strict' || tier === 'close'
        ? "Declare these in the document's :root exactly as written, then reach every colour, font stack and radius through var()."
        : 'These are reference values, not a requirement. Deviate where you like, and declare whatever you settle on in :root.'
    // A withheld role gets its REASON stated rather than being folded into "this theme sets no --a-text".
    // The distinction is real: one is a brand that never named a text colour, the other is a brand whose text
    // colour measured 1.23:1 on its own ground, and only the second tells the model what NOT to reach for.
    const withheld = unreadable.map(
      (role) =>
        `${role.property} is deliberately absent: this brand's value for it (${role.value}) measures ${role.ratio.toFixed(2)}:1 against ${role.ground}, which is unreadable. Do not use ${role.value} for text anywhere.`,
    )
    body.push(
      ...section(
        '## Tokens',
        [
          lead,
          '```tokens',
          ...tokens.map(([property, value]) => `${property}: ${value}`),
          '```',
          ...withheld,
          missing.length > 0
            ? `This theme sets no ${missing.join(', ')}. Choose ${missing.length === 1 ? 'that one' : 'those'} yourself, declare ${missing.length === 1 ? 'it' : 'them'} in the same :root block, and hold the choice across the whole document.`
            : '',
        ].filter((line) => line !== ''),
      ),
    )
  }

  body.push(...section('## Colour', recipeColorLines(style, vars, invented, tier)))
  body.push(...section('## Type', recipeTypeLines(style)))
  body.push(...section('## Density and space', recipeSpaceLines(style)))
  body.push(...section('## Shape', recipeRadiusLines(style, vars)))
  body.push(...section('## Depth', recipeDepthLines(style)))

  // VOICE BEFORE STRUCTURE, and never dropped (moved 2026-09-05). Tone, examples and avoids are the half
  // of a brand the tokens cannot state, and they used to sit last behind the guideline, where a long art
  // direction pushed them to the bottom of the budget. The guideline is its own section below, and it is
  // the droppable one.
  body.push(...section('## Voice', recipeVoiceLines(style)))

  const patterns = style.layout.patterns
    .map((p) => PATTERN_RULE[p])
    .filter((p) => p !== undefined)
    .slice(0, MAX_RECIPE_STRUCTURES)
  body.push(
    ...section('## Structure', [
      ...(patterns.length ? ["Reach for these, best first, and pick the one that fits this artifact's argument:"] : []),
      ...patterns.map((p) => `- ${p}`),
    ]),
  )

  // Material AFTER the tokens and the type rules, because that is the order the model should read them in: a
  // photograph placed before the palette is decided is a photograph the palette then has to accommodate.
  body.push(...section('## Logo', recipeLogoLines(style, vars, assets), DROP_LOGO))
  body.push(...section('## Site material', recipeAssetLines(assets), DROP_MATERIAL))

  // Rules BEFORE Voice, at the never-drop tier. A rule is the one instruction in this recipe that came from
  // the user's own mouth after seeing a document get it wrong, so it cannot be the section that loses to a
  // long guideline, and it must not read as a footnote to the tone.
  body.push(...section('## Rules', ruleLines(style)))

  // The guideline is clamped BEFORE it is rendered, so an oversized art direction costs itself rather than
  // costing the whole section to the budget drop below — and the section is the first the fit drops.
  const guideline = style.voice.guideline?.trim()
  const direction =
    guideline && guideline.length > MAX_RECIPE_GUIDELINE
      ? `${guideline.slice(0, MAX_RECIPE_GUIDELINE)}\n(art direction truncated)`
      : guideline
  body.push(...section('## Art direction', direction ? [DIRECTION[tier], direction] : [], DROP_GUIDELINE))
  body.push(...section('## The set', recipeSetLines(docCount)))

  // Nothing but a heading would be worse than silence: it spends the THEME CONTEXT framing on an empty
  // promise, and the model then has to invent the whole aesthetic anyway. Same bail as styleToPromptBlock,
  // and it runs BEFORE the manifest line, which is about the theme rather than part of it — a recipe
  // carrying only a manifest instruction is still a recipe that says nothing.
  //
  // A DEFAULT SCALE ALONE IS NOT A BRAND. Every kit now carries five sizes (`styleFromBrandKit` derives
  // them so the panel and the document agree), and for a kit that knows nothing else those five are our
  // artifact-scale anchors, not a fact about the brand. A recipe made of them would turn "this kit has
  // nothing to style with yet" — the refusal the kit page and the workspace both rely on — into a
  // generation dressed in our defaults and called the brand. So a Type section whose only content is a
  // `default`-ranked scale does not count as having said something.
  const onlyDefaultRoles =
    roles !== undefined &&
    style.provenance['type.roles']?.confidence === 'default' &&
    recipeTypeLines({ ...style, type: { ...style.type, roles: undefined } }).length === 0
  if (body.length === 0 || (onlyDefaultRoles && body.every((s) => s.text.startsWith('## Type')))) return ''

  // Only where the framing actually asks for adoption. At light/faint the document is told to prioritise its
  // own clean design, and `theme:"deck"` is the flag the deterministic re-theme reads to decide it may
  // rewrite this document's tokens — claiming it for a document that deliberately went its own way is a lie
  // the canvas would later act on.
  if (tier === 'strict' || tier === 'close') {
    body.push({
      text: '## Manifest\nThis document follows the deck theme, so set "theme":"deck" in the manifest, and name the one aesthetic you commit to in "aesthetic".',
      drop: DROP_NEVER,
    })
  }

  // SECOND, after the header and before the tokens — so everything that follows is read as the system it
  // names. Built after the empty-body bail above: a lock over nothing would be a recipe that says nothing
  // and claims it is locked.
  const system: RecipeSection[] =
    lock !== false && (tier === 'strict' || tier === 'close') ? [{ text: `## System\n${SYSTEM_LOCK}`, drop: DROP_NEVER }] : []

  return fitRecipe(
    [{ text: `# ${style.name.trim() || 'Deck theme'}\n${RECIPE_FRAMING[tier]}`, drop: DROP_NEVER }, ...system, ...body],
    budget,
  )
}

/**
 * WHAT THIS ONE DOCUMENT IS FOR — one line, for one document in a run of `count`.
 *
 * The artifact target generates ONE document today and Guri wants two or three. Three generations from one
 * theme and one brief is not three documents, it is three attempts at the same document: the model has no way
 * to know which part of the argument it was handed, so it writes the whole argument every time and the set
 * reads as three drafts.
 *
 * THE VOCABULARY IS DECK'S OWN, not a new one. `deckJudgment.server.ts` decides three things about a slide —
 * THE ONE IDEA, THE SIGNATURE DEVICE ("a split, a spectrum, a field, an orbit, a ledger, a single number"),
 * THE VERTICAL BUDGET — and this names the first two, with the device glossed from {@link PATTERN_RULE} so the
 * thing that PICKS a shape and the thing that GRADES one are still reading the same sentence. Restated rather
 * than imported: that file is `.server.ts` precisely so its prompt never reaches a browser bundle, and this
 * module does.
 *
 * Returned as a line to APPEND TO THE BRIEF, not to the theme recipe: the recipe is shared by every document
 * in the set and describes the dress, while this is the one thing that differs per document.
 */
interface ArtifactAngle {
  /** THE ONE IDEA, in the deck-judgment sense: what this document carries and no other one does. */
  carries: string
  /** The signature devices that make that job legible. Two, so the model still chooses. */
  devices: LayoutPattern[]
}

/**
 * The ladder, in the order an argument is actually built.
 *
 * Five rather than nine: past the close there is nothing left to say that is not a repeat, and a set larger
 * than five wraps rather than inventing a sixth job it does not have. The device pairs are chosen so that
 * consecutive documents never share one, which is what makes "not the structure your neighbour used" an
 * instruction the model can follow rather than a constraint it has to solve.
 */
const ARTIFACT_ANGLES: ArtifactAngle[] = [
  {
    carries: 'THE CLAIM, the one sentence the whole set exists to make and the one that survives if the rest is cut',
    devices: ['single-number', 'full-bleed'],
  },
  {
    carries: 'THE EVIDENCE, what makes that claim checkable instead of asserted',
    devices: ['ledger', 'field'],
  },
  {
    carries: 'THE MECHANISM, how the thing actually works, shown rather than described',
    devices: ['orbit', 'stack'],
  },
  {
    carries: 'THE CONTRAST, what this replaces and what changes the moment it does',
    devices: ['split', 'spectrum'],
  },
  {
    carries: 'THE CLOSE, the one thing left standing after the screen goes dark',
    devices: ['grid', 'single-number'],
  },
]

/** The description half of a `PATTERN_RULE` ("aligned rows of fact, columns lined up…"), so an angle can name
 *  a device and say what it is in the same breath. Derived, never restated: two glosses for one pattern is how
 *  the picker and the grader drift apart. */
export function patternGloss(pattern: LayoutPattern): string {
  const rule = PATTERN_RULE[pattern]
  const dash = rule.indexOf('—')
  return dash < 0 ? rule : rule.slice(dash + 1).trim()
}

/**
 * One line naming what THIS document is for, so a run of three produces three documents rather than three
 * attempts at one. `index` is 0-based. Out-of-range and non-finite inputs are clamped rather than thrown on:
 * this rides a generation loop, and a bad index must cost a worse line, never a failed run.
 */
export function artifactAngle(index: number, count: number): string {
  const total = Number.isFinite(count) ? Math.max(1, Math.floor(count)) : 1
  const position = Number.isFinite(index) ? Math.min(Math.max(0, Math.floor(index)), total - 1) : 0

  if (total === 1) {
    // One document carries the whole argument, which is the judgment prompt's own first instruction. The
    // device list is named in full because there is no neighbour to differentiate from.
    return 'This is the only document, so it carries the whole argument: name THE ONE IDEA, the one that survives if the others are cut, and build the artifact on the signature device that makes it legible at a glance — a split, a spectrum, a field, an orbit, a ledger, a single number. Never fall back to a bulleted list.'
  }

  const angle = ARTIFACT_ANGLES[position % ARTIFACT_ANGLES.length]
  // "a orbit" reads as a typo, and a line the model half-trusts is a line it half-follows. Vowel test only:
  // every pattern name is one of nine known words, so there is no `hour`/`university` case to get wrong.
  const devices = angle.devices
    .map((d) => `${/^[aeiou]/.test(d) ? 'an' : 'a'} ${d} (${patternGloss(d)})`)
    .join(' or ')
  return `Document ${position + 1} of ${total} carries ${angle.carries}. Build it as ${devices}, never on the structure a neighbouring document uses, and never as a bulleted list.`
}

/**
 * The composition block an ARTIFACT's prompt carries, built from the vocabulary above.
 *
 * WHY THE PROMPT AND NOT THE RECIPE. The theme recipe is framed by the route as owning palette, fonts,
 * weights, density and voice, and it is clamped, strength-tiered and skipped outright for kit-less decks. The
 * contract's OPEN MODE condition is that "the REQUEST names a STRUCTURE" — the request is the prompt, which
 * is rendered last and uncapped. So structure rides the prompt, the way the lab proved the angle line does.
 *
 * Two postures. A RESOLVED family (the user's own words, the outline's `Structure:` line, or the planner)
 * names the shape and its gloss — the gloss is what trips OPEN MODE for the five families a vertical stack
 * cannot express. An UNRESOLVED one carries the whole menu with its content triggers, what the set has
 * already used, and the distinct-count rule, so the writer chooses from the content in front of it. Edits
 * carry nothing: the caller sends an empty options object and gets an empty string back via `family`
 * absent AND nothing else set — callers gate on their own edit flag instead.
 */
export interface CompositionBriefOptions {
  /** Decided upstream: the user's words, the outline's `Structure:` line, or the layout planner. */
  family?: LayoutPattern
  /** The planner's beat for this document, when it named one. */
  carries?: string
  /** Families of the OTHER documents in the set, deck order, deduped. */
  used?: LayoutPattern[]
  /** The nearest non-blank neighbour before / after, when its family is known. */
  before?: LayoutPattern
  after?: LayoutPattern
  /** The set's size. 2+ adds the distinct-count rule; absent or 1 omits it. */
  docCount?: number
}

const article = (pattern: LayoutPattern): string => (/^[aeiou]/.test(pattern) ? 'an' : 'a')

export function compositionBrief(opts: CompositionBriefOptions): string {
  if (opts.family) {
    const carries = (opts.carries ?? '').trim().replace(/\s+/g, ' ').replace(/\.+$/, '')
    return [
      `STRUCTURE. Build this document as ${article(opts.family)} ${opts.family} — ${patternGloss(opts.family)}.`,
      carries ? `It carries "${carries}".` : '',
      'The shape is the composition: build it first, then put the real content into it, never under a heading above a stack.',
      NO_LIST_RULE,
    ]
      .filter(Boolean)
      .join(' ')
  }

  const lines: string[] = [
    `STRUCTURE. Build this document on ONE of these ${PATTERN_ORDER.length} structures. Choose it from what this document has to say, then build the whole composition on it:`,
    ...PATTERN_ORDER.map((p) => `- ${PATTERN_RULE[p]}. Reach for it when ${PATTERN_WHEN[p]}.`),
  ]
  const used = [...new Set(opts.used ?? [])]
  const neighbours = [
    opts.before ? `not ${article(opts.before)} ${opts.before} (before it)` : '',
    opts.after ? `not ${article(opts.after)} ${opts.after} (after it)` : '',
  ].filter(Boolean)
  if (used.length || neighbours.length) {
    lines.push(
      [
        used.length ? `Structures already used in this set, in deck order: ${used.join(', ')}.` : '',
        neighbours.length ? `Never the structure of the document beside this one: ${neighbours.join(', ')}.` : '',
        used.length ? 'Prefer one the set has not used yet.' : '',
      ]
        .filter(Boolean)
        .join(' '),
    )
  }
  const n = typeof opts.docCount === 'number' && Number.isFinite(opts.docCount) ? Math.floor(opts.docCount) : 0
  if (n >= 2) {
    lines.push(
      n <= 3
        ? `This set has ${n} documents: give every document a different structure.`
        : `This set has ${n} documents: use at least ${Math.min(4, n)} different structures across it, and never the same one on two documents in a row.`,
    )
  }
  lines.push(
    "The theme above is the set's identity and does not move: same tokens, same two fonts, same scale on every document. The structure is what moves.",
  )
  lines.push(NO_LIST_RULE)
  return lines.join('\n')
}

/**
 * Style → the `BrandStyleTokens` the LIVE generation route already takes.
 *
 * The bridge back, so `/style-lab` can drive production generation from a Style without rewriting the route.
 * Deliberately lossy — `BrandStyleTokens` has no field for most of what a Style knows:
 *
 *   - `space.unit`, `space.density`, `layout.patterns`, `shadow`, `type.scale` — DROPPED. The live token
 *     shape has no vocabulary for any of them; they reach a model only through {@link styleToPromptBlock}.
 *     This is the largest loss and the reason the lab exists: it is the measurable difference between the two
 *     paths, and the case for eventually flipping generation onto the record.
 *   - `radius.sm` / `radius.lg` — DROPPED. `typography.radius` is one value; md wins (see radiusLines).
 *   - `voice` — DROPPED. In the live pipeline prose rides a separate block (`buildMoodboardPromptBlock`),
 *     never the brand tokens, so carrying it here would state it twice in one prompt.
 *   - `assets.fontFaces` — DROPPED. The live path derives only WEIGHTS from faces (`headingFaceWeights`), and
 *     those already travel as `type.displayWeights` → `headingFontWeights`. The face URLs are loaded by the
 *     renderer from its own catalog, never from these tokens.
 *   - `provenance` — DROPPED. The live route has no notion of confidence; that is precisely the gap the record
 *     closes, and it closes it earlier, at merge time.
 *   - `typography.headingSize` / `bodySize` — NEVER EMITTED. A Style holds a ratio, not absolute sizes. The
 *     live serializer wants headingSize only to normalise px/rem tracking into em, and this always emits em.
 *
 * `headingWeight` is left unset on purpose: `buildBrandStyleBlock` picks the heaviest SHIPPED weight when it
 * is missing, which is the correct answer and the one that cannot produce a faux bold. Naming a weight here
 * would only be a chance to name one the font files do not contain.
 *
 * An all-empty Style yields an all-empty object rather than undefined, and callers need no special case:
 * `buildBrandStyleBlock` already returns '' when palette and roles are both empty.
 */
export function styleToBrandStyleTokens(style: Style): BrandStyleTokens {
  const roles: Record<string, string> = {}
  if (style.color.bg) roles.background = style.color.bg
  if (style.color.surface) roles.surface = style.color.surface
  if (style.color.text) roles.text = style.color.text
  if (style.color.muted) roles.textMuted = style.color.muted
  if (style.color.accent) roles.accent = style.color.accent
  if (style.color.border) roles.border = style.color.border

  const typography: Record<string, string> = {}
  // `none` is not emitted: it would spend a whole instruction line telling the model to keep doing what it was
  // already doing.
  if (style.type.case === 'upper') typography.headingTransform = 'uppercase'
  // Emitted in em, already clamped. `buildBrandStyleBlock` normalises px/rem against a heading SIZE read from
  // this same record — a size `Style` deliberately does not carry — so handing it em means that conversion is
  // already done, while its own slide-safe clamp still runs on top.
  const tracking = safeTracking(style.type.tracking)
  if (tracking) typography.headingLetterSpacing = tracking
  if (style.type.leading != null && Number.isFinite(style.type.leading)) {
    typography.headingLineHeight = String(style.type.leading)
  }
  if (style.radius.md != null && Number.isFinite(style.radius.md)) typography.radius = `${style.radius.md}px`

  const palette = style.color.palette.filter(isHex)
  const weights = (style.type.displayWeights ?? []).filter((w) => /^\d{3}$/.test(w))

  return {
    palette: palette.length ? palette : undefined,
    roles: Object.keys(roles).length ? roles : undefined,
    headingFont: style.type.display,
    bodyFont: style.type.body,
    typography: Object.keys(typography).length ? typography : undefined,
    headingFontWeights: weights.length ? weights : undefined,
    logoUrl: style.assets.logoUrl,
  }
}
